/**
 * TeachBud School Branding
 *
 * Stores optional school/teacher identification fields that appear in the
 * header of Lesson Plans and Activity Sheets.  These fields are purely
 * presentational — they never influence instructional content or the
 * LessonBlueprint.
 *
 * Implementation note: logoDataUrl stores the image as a base64 data URL in
 * component state.  This is acceptable for initial release but should be
 * migrated to managed object storage when logos routinely exceed ~200 KB,
 * to avoid localStorage quota pressure and render-blocking inline data.
 */

export interface BrandingData {
  schoolName: string;
  districtName: string;
  teacherName: string;
  courseClass: string;
  period: string;
  schoolYear: string;
  /** Base64 data URL — typically a PNG or JPEG logo.  Empty string = no logo. */
  logoDataUrl: string;
  /** Which fields to actually render on the resource header. */
  visibleFields: {
    schoolName: boolean;
    districtName: boolean;
    teacherName: boolean;
    courseClass: boolean;
    period: boolean;
    schoolYear: boolean;
    logo: boolean;
  };
}

export function defaultBranding(): BrandingData {
  return {
    schoolName: '',
    districtName: '',
    teacherName: '',
    courseClass: '',
    period: '',
    schoolYear: '',
    logoDataUrl: '',
    visibleFields: {
      schoolName: true,
      districtName: false,
      teacherName: true,
      courseClass: true,
      period: false,
      schoolYear: false,
      logo: false,
    },
  };
}

/** Returns true if any branding field has content that would render on the resource. */
export function hasBranding(b: BrandingData): boolean {
  return (
    (b.visibleFields.schoolName && !!b.schoolName) ||
    (b.visibleFields.districtName && !!b.districtName) ||
    (b.visibleFields.teacherName && !!b.teacherName) ||
    (b.visibleFields.courseClass && !!b.courseClass) ||
    (b.visibleFields.period && !!b.period) ||
    (b.visibleFields.schoolYear && !!b.schoolYear) ||
    (b.visibleFields.logo && !!b.logoDataUrl)
  );
}
